import React, { memo } from 'react';

import { CheckMark } from 'icons';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { Copy } from 'ui/atoms';

export type CheckBoxProps = {
  className?: string;
  labelClassName?: string;
  label?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  theme?: 'primary' | 'info' | 'dark';
  isError?: boolean;
  indeterminate?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export function CheckBox({
  className,
  labelClassName,
  disabled,
  label,
  readOnly,
  theme = 'primary',
  isError,
  indeterminate = false,
  ...rest
}: CheckBoxProps) {
  const checkedThemeStyling = match(theme)
    .with(
      'primary',
      () => 'bg-primary border-primary drop-shadow-[0px_2px_4px_rgba(75,180,179,0.2)]',
    )
    .with('info', () => 'bg-v2blue border-v2blue drop-shadow-[0px_2px_4px_rgba(0,122,255,0.2)]')
    .with(
      'dark',
      () => 'bg-v2blueGray-800 border-v2blueGray-800 drop-shadow-[0px_2px_4px_rgba(35,56,65,0.2)]',
    )
    .exhaustive();

  const inputId = rest.id || rest.name;

  return (
    <div className={twMerge('cursor-pointer', disabled && 'cursor-not-allowed', className)}>
      <input
        id={inputId}
        {...rest}
        data-testid="outvioui--checkbox-input"
        className="hidden"
        type="checkbox"
        data-cy="checkbox"
        name={rest?.name}
        readOnly={readOnly}
        disabled={disabled}
      />

      <label
        htmlFor={inputId}
        className={twMerge(
          'cursor-pointer flex items-center',
          disabled && 'opacity-40 cursor-not-allowed',
        )}
      >
        <div className="-mt-[2px] w-[18px] h-[18px] min-w-[18px] min-h-[18px] block relative select-none">
          <span
            className={twMerge(
              'absolute inset-0 pb-2 h-full w-full rounded-md border-2 pointer-events-none',
              (rest.value || rest.checked) && checkedThemeStyling,
              !(rest.value || rest.checked) && 'bg-white border-v2blueGray-200',
              isError && 'border-v2red',
              indeterminate && checkedThemeStyling,
            )}
          />
          {(rest.value || rest.checked) && !indeterminate && (
            <CheckMark
              width="11"
              height="11"
              className="absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-60%] pointer-events-none text-white"
            />
          )}

          {indeterminate && (
            <span className="rounded-full absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-60%] w-2.5 h-[2px] bg-white" />
          )}
        </div>
        <Copy as="span" className={twMerge('ml-2 text-v2blueGray-700', labelClassName)}>
          {label}
        </Copy>
      </label>
    </div>
  );
}

export default memo(CheckBox);
